import type { BoxProps } from '@mantine/core'
import { Box, Button, Group, Progress, Text } from '@mantine/core'
import type { DropzoneProps } from '@mantine/dropzone'
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import type { IconProps } from '@tabler/icons-react'
import { IconArrowDown, IconCloudUpload, IconX } from '@tabler/icons-react'
import { inflect } from 'inflection'
import type { ComponentType, ReactNode } from 'react'
import { useRef } from 'react'

import type { EmptyStateProps } from '@/components/EmptyState'
import { EmptyState } from '@/components/EmptyState'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/FileUpload.css'

const pdfMimeType = 'application/pdf'

export const textMimeTypes = [
  'text/plain',
  //'text/markdown'
]

export const videoMimeTypes = [
  'video/mp4',
  'video/quicktime', // .mov
]

export const imageMimeTypes = ['image/png', 'image/jpeg', 'image/tiff']

export const acceptedFileUploadText =
  'Drag and drop files or browse for them using the button below.\r\nValid file types include: png, jpg, tiff, mp4, mov, txt, pdf, pptx and docx.'
export const rejectedFileUploadText = 'Only png, jpg, tiff, mp4, mov, txt, pdf, pptx and docx files are supported.'
export const needsExtractionMimeTypes = [
  pdfMimeType,
  'text/plain',
  // docx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // pptx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // video files
  ...videoMimeTypes,
  // image files
  ...imageMimeTypes,
]
export const supportsProgressMimeTypes = [pdfMimeType]
export const needsMetadataGenerationMimeTypes = [...videoMimeTypes, ...imageMimeTypes]

// Using set to remove duplicates
export const contentUploadMimeTypes = [...new Set([...textMimeTypes, ...needsExtractionMimeTypes])]
// For media files, we use only MIME types (extensions cause console warnings in Dropzone)
export const mediaUploadMimeTypes = [...new Set([...videoMimeTypes, ...imageMimeTypes])]
// Unified upload mime types for all file types (text, media, office docs)
export const allUploadMimeTypes = [...new Set([...contentUploadMimeTypes, ...mediaUploadMimeTypes])]

// TODO: Svgs were removed as a precaution, there was potentially an issue rendering them with iOS 17, though we didn't fully investigate.
// See SME-152 https://sidekickwellness.atlassian.net/browse/SME-152?atlOrigin=eyJpIjoiMzUwOWY5ZTU4M2NjNDM5OGE3NGU5M2ZjNGQ0MGNiOGQiLCJwIjoiaiJ9
export const ALLOWED_IMAGE_TYPES = IMAGE_MIME_TYPE.filter(type => type !== 'image/svg+xml')

export interface FileUploadProps extends Omit<DropzoneProps, 'openRef' | 'title'> {
  WrapperProps?: BoxProps
  EmptyStateProps?: EmptyStateProps
  titles?: {
    idle?: string
    accept?: string
    reject?: string
  }
  icons?: {
    idle?: ComponentType<IconProps>
    accept?: ComponentType<IconProps>
    reject?: ComponentType<IconProps>
  }
  hint?: ReactNode
  hints?: {
    idle?: ReactNode
    accept?: ReactNode
    reject?: ReactNode
  }
  primaryActionLabel?: string
  secondaryActionLabel?: string
  onSecondaryActionClick?: () => void
  tertiaryActionLabel?: string
  onTertiaryActionClick?: () => void
  actionHidden?: boolean
  BottomSlot?: ReactNode
  TopSlot?: ReactNode
  label?: string
  error?: string
  description?: string
  withAsterisk?: boolean
  processing?: boolean
  processProgress?: number
  // Cancellation support
  onCancelClick?: () => void
  showCancelButton?: boolean
}

export function FileUpload(props: FileUploadProps) {
  const {
    WrapperProps,
    titles,
    hint,
    hints,
    icons,
    error,
    description,
    label,
    maxFiles = 10,
    withAsterisk,
    primaryActionLabel,
    actionHidden,
    BottomSlot = null,
    TopSlot = null,
    EmptyStateProps,
    loading,
    processing,
    processProgress = 0,
    secondaryActionLabel,
    onSecondaryActionClick,
    tertiaryActionLabel,
    onTertiaryActionClick,
    // Cancellation support
    onCancelClick,
    showCancelButton,
    ...DropzoneProps
  } = props

  const {
    idle: IconIdle = IconCloudUpload,
    accept: IconAccept = IconArrowDown,
    reject: IconReject = IconX,
  } = icons ?? {}

  const hasControls = !actionHidden
  const defaultHint = hint ?? `Drag & drop ${inflect('file', maxFiles)} here to upload.`

  const openRef = useRef<() => void>(null)

  return (
    <Box>
      <Box
        {...WrapperProps}
        className={theme.cx(classes.wrapper, WrapperProps?.className, hasControls && classes.wrapperWControls)}>
        {label && <InputLabel label={label} withAsterisk={withAsterisk} />}

        <Box className={classes.dropzoneWrapper}>
          {processing && (
            <Box className={classes.progressOverlay}>
              <Progress
                value={processProgress === 0 ? 100 : processProgress}
                size="xl"
                radius="md"
                color={theme.colors.primaryColors[6]}
                animated
                style={{ width: '100%', height: '12px' }}
              />
              {showCancelButton && onCancelClick && (
                <Button size="sm" variant="subtle" color="red" onClick={onCancelClick} style={{ marginTop: '8px' }}>
                  Cancel Upload
                </Button>
              )}
            </Box>
          )}
          <Dropzone
            radius="md"
            maxFiles={maxFiles}
            {...DropzoneProps}
            loading={loading && !processing}
            openRef={openRef}
            classNames={{
              root: theme.cx(classes.dropzone, DropzoneProps.className, !!error && classes.dropzoneWError),
              inner: theme.cx(
                classes.dropzoneInner,
                hasControls && classes.dropzoneInnerWControls,
                DropzoneProps?.activateOnClick === false && classes.dropzoneInnerWPointerEvents,
              ),
            }}>
            {TopSlot}

            <Dropzone.Idle>
              <EmptyState
                size="sm"
                title={titles?.idle ?? `Upload ${inflect('File', maxFiles)}`}
                description={hints?.idle ?? defaultHint}
                Icon={IconIdle}
                DescriptionProps={{ maw: '100%' }}
                {...EmptyStateProps}
              />
            </Dropzone.Idle>

            <Dropzone.Accept>
              <EmptyState
                size="sm"
                title={titles?.accept ?? `Drop ${inflect('File', maxFiles)} Here...`}
                description={hints?.accept ?? defaultHint}
                Icon={IconAccept}
                IconProps={{ color: theme.colors.primaryColors[6] }}
                DescriptionProps={{ maw: '100%' }}
                {...EmptyStateProps}
              />
            </Dropzone.Accept>

            <Dropzone.Reject>
              <EmptyState
                size="sm"
                title={titles?.reject ?? `Unsupported ${inflect('File', maxFiles)} Type or Size...`}
                description={hints?.reject ?? defaultHint}
                Icon={IconReject}
                IconProps={{ color: theme.colors.error }}
                DescriptionProps={{ maw: '100%' }}
                {...EmptyStateProps}
              />
            </Dropzone.Reject>

            {BottomSlot}
          </Dropzone>
        </Box>

        {hasControls && (
          <Group gap="xl" className={theme.cx(classes.controlsWrapper, loading && classes.controlsWhileLoading)}>
            <Button className={classes.selectControl} onClick={() => openRef.current?.()}>
              {primaryActionLabel ?? `Select ${inflect('File', maxFiles)}`}
            </Button>

            {secondaryActionLabel && (
              <Button
                size="md"
                variant="light"
                className={theme.cx(classes.selectControl, classes.secondaryAction)}
                onClick={onSecondaryActionClick}>
                {secondaryActionLabel}
              </Button>
            )}

            {tertiaryActionLabel && (
              <Button
                size="md"
                variant="light"
                className={theme.cx(classes.selectControl, classes.secondaryAction)}
                onClick={onTertiaryActionClick}>
                {tertiaryActionLabel}
              </Button>
            )}
          </Group>
        )}
      </Box>

      {error ? (
        <Text className={classes.error}>{error}</Text>
      ) : description ? (
        <Text c="dimmed" className={classes.description}>
          {description}
        </Text>
      ) : null}
    </Box>
  )
}

// YULIANTODO: When implementing the file upload endpoints, we can add a upload progress tracker and use: https://mantine.dev/x/nprogress/
